import './searchAssistantView.scss';
import {useSearchPageType, useStateLastSearchedQuery} from '../../search/search-hooks';
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getUuidForSearchRequest,} from '../common/utils';
import {DefaultRootState, useDispatch, useSelector} from 'react-redux';
import {getSearchRequest} from '../../search/search.utils';
import {AiAssistantConversation, ChatType, Role} from '../common/types';
import {useStateConversation} from '../duck/assistant-hooks';
import {doCreateConversationRequest, doLoadConversationRequest,} from '../duck/assistant-actions';
import {SearchAssistantContent} from './searchAssistantContent';
import {SearchAssistantHeader} from './searchAssistantHeader';

export type Props = {
    onCloseRequest: () => void;
};

export const SearchAssistantView = ({onCloseRequest}: Props): React.JSX.Element | null => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const queryTerm = useStateLastSearchedQuery();
    const state: DefaultRootState = useSelector((state: DefaultRootState) => state);
    const searchPageType = useSearchPageType();
    const [activeSearchRequest, setActiveSearchRequest] = useState(() => {
        return getSearchRequest(() => state, queryTerm, false, 0);
    });

    const conversationId: string = getUuidForSearchRequest(activeSearchRequest);
    const conversation: AiAssistantConversation | undefined = useStateConversation(conversationId);
    const isLoadingConversations = useSelector((state: DefaultRootState) => state.assistant.isLoadingConversations);


    useEffect(() => {
        messagesEndRef?.current?.scrollIntoView({behavior: 'smooth'});
    }, []);

    // Load conversation if it exists in DB
    useEffect(() => {
        if (!conversation) {
            dispatch(doLoadConversationRequest(conversationId));
        }
    }, [conversationId, conversation, dispatch]);

    // Create a new conversation if it doesn't exist
    useEffect(() => {
        if (!conversation && !isLoadingConversations) {
            dispatch(
                doCreateConversationRequest(
                    ChatType.SEARCH,
                    conversationId,
                    t('document-assistant.welcome'),
                    Role.ASSISTANT,
                    activeSearchRequest,
                ),
            );
        }
    }, [activeSearchRequest, conversation, conversationId, dispatch, t, isLoadingConversations]);

    useEffect(() => {
        const newSearchRequest = getSearchRequest(() => state, queryTerm, false, 0);
        setActiveSearchRequest(newSearchRequest);
    }, [queryTerm, searchPageType, state]);


    return (
        <>
            <SearchAssistantHeader onCloseRequest={onCloseRequest}
                                   duplicatesCount={conversation?.totalDuplicatesCount}></SearchAssistantHeader>
            <SearchAssistantContent conversation={conversation} conversationId={conversationId}
                                    activeSearchRequest={activeSearchRequest}></SearchAssistantContent>
        </>
    );
};
